import { create } from 'zustand';
import type { LocationState } from './location.types';
import { 
  addSavedAddress as addSavedAddressAction,
  updateSavedAddress as updateSavedAddressAction,
  removeSavedAddress as removeSavedAddressAction,
  selectAddress as selectAddressAction,
  clearSelectedAddress as clearSelectedAddressAction
} from './location-actions';
import { 
  syncWithDatabase,
  checkAuthAndLoad,
  loadUserAddresses
} from './location-db';
import { 
  loadFromStorage,
  saveToStorage,
  clearLocationData
} from './location-storage';

// Re-export types for backwards compatibility
export type { LocationCoordinate, AddressInfo, SavedAddress } from './location.types';

export const useLocationStore = create<LocationState>((set, get) => ({
  // 현재 위치 정보
  currentLocation: {
    coordinate: null,
    address: null
  },
  
  // 저장된 주소 목록
  savedAddresses: [],
  
  // 선택된 주소 ID
  selectedAddressId: null,
  
  // 로딩 상태
  isLocationLoading: false,
  isAddressLoading: false,
  
  // 에러 상태
  locationError: null,
  
  // 기본 액션들
  setCurrentLocation: (coordinate, address) => {
    set({
      currentLocation: { coordinate, address },
      locationError: null
    });
  },
  
  setLocationLoading: (loading) => {
    set({ isLocationLoading: loading });
  },
  
  setAddressLoading: (loading) => {
    set({ isAddressLoading: loading });
  },
  
  setLocationError: (error) => {
    set({ locationError: error });
  },
  
  // 주소 관리 액션들 (분리된 모듈의 함수들을 연결)
  addSavedAddress: async (addressData) => {
    const state = get();
    await addSavedAddressAction(addressData, state, (updates) => {
      set((currentState) => ({ ...currentState, ...updates }));
    });
  },
  
  updateSavedAddress: async (id, updates) => {
    const state = get();
    await updateSavedAddressAction(id, updates, state, (stateUpdates) => {
      set((currentState) => ({ ...currentState, ...stateUpdates }));
    });
  },
  
  removeSavedAddress: async (id) => {
    const state = get();
    await removeSavedAddressAction(id, state, (updates) => {
      set((currentState) => ({ ...currentState, ...updates }));
    });
  },
  
  selectAddress: async (id) => {
    const state = get();
    await selectAddressAction(id, state, (updates) => {
      set((currentState) => ({ ...currentState, ...updates }));
    });
  },
  
  clearSelectedAddress: async () => {
    const state = get();
    await clearSelectedAddressAction(state, (updates) => {
      set((currentState) => ({ ...currentState, ...updates }));
    });
  },
  
  // 데이터 로드/저장 액션들
  loadFromStorage: () => {
    const storageData = loadFromStorage();
    if (storageData) {
      set({
        currentLocation: storageData.currentLocation,
        savedAddresses: storageData.savedAddresses,
        selectedAddressId: storageData.selectedAddressId
      });
    }
  },
  
  saveToStorage: async () => {
    const state = get();
    await saveToStorage({
      currentLocation: state.currentLocation,
      savedAddresses: state.savedAddresses,
      selectedAddressId: state.selectedAddressId
    });
  },
  
  clearLocationData: () => {
    clearLocationData();
    set({
      currentLocation: { coordinate: null, address: null },
      savedAddresses: [],
      selectedAddressId: null,
      locationError: null
    });
  },
  
  // 데이터베이스 동기화 액션들
  loadUserAddresses: async (userId) => {
    set({ isAddressLoading: true, locationError: null });
    try {
      const result = await loadUserAddresses(userId);
      
      if (result.error) {
        set({ locationError: result.error });
      } else {
        set({
          savedAddresses: result.savedAddresses,
          selectedAddressId: result.selectedAddressId,
          currentLocation: result.currentLocation
        });
      }
    } catch (error) {
      console.error('Failed to load user addresses:', error);
      set({ locationError: '주소 목록을 불러오는데 실패했습니다.' });
    } finally {
      set({ isAddressLoading: false });
    }
  },
  
  syncWithDatabase: async () => {
    set({ isAddressLoading: true, locationError: null });
    try {
      const result = await syncWithDatabase();
      
      if (result.error) {
        set({ locationError: result.error });
      } else {
        set({
          savedAddresses: result.savedAddresses,
          selectedAddressId: result.selectedAddressId,
          currentLocation: result.currentLocation
        });
      }
    } catch (error) {
      console.error('Failed to sync with database:', error);
      set({ locationError: '데이터 동기화에 실패했습니다.' });
    } finally {
      set({ isAddressLoading: false });
    }
  },
  
  checkAuthAndLoad: async () => {
    set({ isAddressLoading: true, locationError: null });
    try {
      const result = await checkAuthAndLoad();
      
      if (result.error) {
        set({ locationError: result.error });
      } else {
        set({
          savedAddresses: result.savedAddresses,
          selectedAddressId: result.selectedAddressId,
          currentLocation: result.currentLocation
        });
      }
    } catch (error) {
      console.error('Failed to check auth and load:', error);
      set({ locationError: '주소 데이터 로드에 실패했습니다.' });
    } finally {
      set({ isAddressLoading: false });
    }
  }
}));